(function () {
  'use strict';

  const instances = new Map();
  const MOBILE_MQ = '(max-width: 768px)';
  const DESKTOP_SLIDE_DURATION_MS = 650;
  const FREEZE_VISIBLE_RATIO = 0.7;
  const SCROLL_KEYS = new Set(['ArrowDown', 'ArrowUp', 'PageDown', 'PageUp', 'Home', 'End', ' ']);

  /** Shared freeze lock — overflow only, never scrollTo / position:fixed. */
  let pageScrollLockCount = 0;
  let pageScrollLockPadding = '';

  function acquirePageScrollLock() {
    if (pageScrollLockCount === 0) {
      const scrollbarWidth = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
      pageScrollLockPadding = scrollbarWidth > 0 ? `${scrollbarWidth}px` : '';
      document.documentElement.classList.add('itshs-page-scroll-lock');
      document.body.classList.add('itshs-page-scroll-lock');
      if (pageScrollLockPadding) {
        document.documentElement.style.paddingRight = pageScrollLockPadding;
      }
    }
    pageScrollLockCount += 1;
  }

  function releasePageScrollLock() {
    if (pageScrollLockCount === 0) return;
    pageScrollLockCount -= 1;
    if (pageScrollLockCount > 0) return;

    document.documentElement.classList.remove('itshs-page-scroll-lock');
    document.body.classList.remove('itshs-page-scroll-lock');
    document.documentElement.style.paddingRight = '';
    pageScrollLockPadding = '';
  }

  function measureHeaderOffset() {
    const storeHeader = document.querySelector('store-header');
    if (storeHeader) {
      const bottom = Math.round(storeHeader.getBoundingClientRect().bottom);
      if (bottom > 0) return bottom;

      const height = Math.round(storeHeader.offsetHeight);
      if (height > 0) return height;
    }

    const headerSection = document.getElementById('shopify-section-header');
    if (headerSection?.offsetHeight > 0) {
      return Math.round(headerSection.offsetHeight);
    }

    const fromVar = parseFloat(
      getComputedStyle(document.documentElement).getPropertyValue('--header-height')
    );
    if (!Number.isNaN(fromVar) && fromVar > 0) return Math.round(fromVar);

    return 0;
  }

  class ImageTextScrollHoverSlider {
    constructor(section) {
      this.section = section;
      this.host =
        section.closest('[id^="shopify-section-"]') ||
        section.closest('.shopify-section') ||
        section;
      this.scrollContainer = section.querySelector('[data-itshs-scroll-container]');
      this.pin = section.querySelector('[data-itshs-pin]');
      this.viewport = section.querySelector('[data-itshs-viewport]');
      this.track = section.querySelector('[data-itshs-track]');
      this.slides = section.querySelectorAll('[data-itshs-slide]');

      if (!this.scrollContainer || !this.pin || !this.viewport || !this.track || !this.slides.length) {
        return;
      }

      this.headerOffset = 0;
      this.maxScroll = 0;
      this.currentIndex = 0;
      this.isMobile = window.matchMedia(MOBILE_MQ);
      this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.wheelLockedUntil = 0;
      this.wheelCooldownMs = DESKTOP_SLIDE_DURATION_MS + 80;
      this.wheelAnimating = false;
      this.wheelAnimTimer = null;
      this.slideTransitionMs = DESKTOP_SLIDE_DURATION_MS;
      this.wheelGestureActive = false;
      this.wheelGestureTimer = null;
      this.wheelGestureIdleMs = 200;
      this.pageScrollLocked = false;
      this.intersectionObserver = null;

      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onModeChange = this.onModeChange.bind(this);
      this.onWheel = this.onWheel.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);

      if (this.prefersReducedMotion) {
        this.section.classList.add('is-reduced-motion');
      }

      this.init();
    }

    init() {
      this.syncHeaderOffset();
      this.measure();
      this.bindEvents();
      this.maybeLockPageScroll();

      window.requestAnimationFrame(() => {
        this.syncHeaderOffset();
        this.measure();
        this.maybeLockPageScroll();
      });

      [100, 300, 800].forEach((delay) => {
        window.setTimeout(() => {
          if (!this.section.isConnected) return;
          this.syncHeaderOffset();
          this.measure();
          this.maybeLockPageScroll();
        }, delay);
      });

      window.requestAnimationFrame(() => {
        this.syncSlideVisualState(this.currentIndex, { animate: false });
        this.section.classList.add('is-itshs-ready');
      });

      if (window.customElements?.whenDefined) {
        customElements.whenDefined('store-header').then(() => {
          if (!this.section.isConnected) return;
          this.syncHeaderOffset();
          this.measure();
          this.maybeLockPageScroll();
        });
      }
    }

    isMobileMode() {
      return this.isMobile.matches;
    }

    syncHeaderOffset() {
      const next = measureHeaderOffset();
      if (next <= 0 && this.headerOffset > 0) return;
      this.headerOffset = next;
      this.section.style.setProperty('--itshs-header-offset', `${next}px`);
    }

    clearScrollContainerHeight() {
      this.scrollContainer.style.height = '';
    }

    measure() {
      this.setSlideWidths();
      this.maxScroll = Math.max(0, this.track.scrollWidth - this.viewport.clientWidth);
      // No travel spacer: section height stays content height.
      this.clearScrollContainerHeight();

      if (this.isMobileMode() || this.prefersReducedMotion) {
        if (!this.isMobileMode()) {
          this.track.style.transform = '';
        }
        return;
      }

      this.setTrackOffset(this.getSlideOffset(this.currentIndex));
    }

    setSlideWidths() {
      const panelWidth = this.viewport.clientWidth;
      this.slides.forEach((slide) => {
        slide.style.flex = `0 0 ${panelWidth}px`;
        slide.style.width = `${panelWidth}px`;
      });
    }

    setTrackOffset(value) {
      const x = Math.max(0, Math.min(this.maxScroll, value));
      this.track.style.transform = x > 0 ? `translate3d(-${x}px, 0, 0)` : '';
    }

    getSlideOffset(index) {
      const slide = this.slides[index];
      return slide ? slide.offsetLeft : 0;
    }

    isCarouselWheelEnabled() {
      if (this.isMobileMode() || this.prefersReducedMotion) return false;
      return this.slides.length > 1;
    }

    /**
     * Freeze when the section is mostly in view (or pinned under header if taller).
     * Does not scrollIntoView / scrollTo — only freezes at the current scrollY.
     */
    shouldFreezePage() {
      if (!this.isCarouselWheelEnabled()) return false;

      const rect = this.host.getBoundingClientRect();
      const viewTop = this.headerOffset;
      const viewBottom = window.innerHeight;
      const availableHeight = Math.max(1, viewBottom - viewTop);
      const contentHeight = rect.height;
      const epsilon = 8;

      if (contentHeight <= availableHeight + epsilon) {
        const visibleTop = Math.max(rect.top, viewTop);
        const visibleBottom = Math.min(rect.bottom, viewBottom);
        const visibleHeight = Math.max(0, visibleBottom - visibleTop);
        const ratio = visibleHeight / contentHeight;
        return ratio >= FREEZE_VISIBLE_RATIO;
      }

      const touchesHeader = rect.top <= viewTop + epsilon;
      const stillVisible = rect.bottom > viewTop + 120;
      return touchesHeader && stillVisible;
    }

    lockPageScroll() {
      if (this.pageScrollLocked || !this.isCarouselWheelEnabled()) return;
      this.pageScrollLocked = true;
      acquirePageScrollLock();
      this.section.classList.add('is-page-scroll-locked');
    }

    unlockPageScroll() {
      if (!this.pageScrollLocked) return;
      this.pageScrollLocked = false;
      releasePageScrollLock();
      this.section.classList.remove('is-page-scroll-locked');
    }

    maybeLockPageScroll() {
      if (this.pageScrollLocked) return;
      if (this.shouldFreezePage()) this.lockPageScroll();
    }

    /** Pointer is over the Shopify section host (covers inner slider + its margin). */
    isPointerOverHost(event) {
      const target = event.target;
      if (!(target instanceof Node)) return false;
      return this.host.contains(target);
    }

    shouldHandleWheel(event) {
      if (this.pageScrollLocked) return true;
      return this.shouldFreezePage() || this.isPointerOverHost(event);
    }

    syncSlideVisualState(nextIndex, options = {}) {
      const prevIndex = this.currentIndex;
      const useDesktopMotion =
        options.animate !== false &&
        !this.isMobileMode() &&
        !this.prefersReducedMotion &&
        this.section.classList.contains('is-itshs-ready');

      if (useDesktopMotion && nextIndex !== prevIndex) {
        this.section.classList.remove('is-slide-forward', 'is-slide-backward');
        this.section.classList.add(nextIndex > prevIndex ? 'is-slide-forward' : 'is-slide-backward');
      }

      this.slides.forEach((slide, index) => {
        slide.classList.toggle('is-active', index === nextIndex);
      });
    }

    goToSlide(index) {
      const maxIndex = this.slides.length - 1;
      const nextIndex = Math.max(0, Math.min(maxIndex, index));

      this.syncSlideVisualState(nextIndex);
      this.currentIndex = nextIndex;
      this.setTrackOffset(this.getSlideOffset(this.currentIndex));
    }

    refreshWheelGesture() {
      this.wheelGestureActive = true;
      if (this.wheelGestureTimer) {
        window.clearTimeout(this.wheelGestureTimer);
      }
      this.wheelGestureTimer = window.setTimeout(() => {
        this.wheelGestureActive = false;
        this.wheelGestureTimer = null;
      }, this.wheelGestureIdleMs);
    }

    clearWheelGesture() {
      this.wheelGestureActive = false;
      if (this.wheelGestureTimer) {
        window.clearTimeout(this.wheelGestureTimer);
        this.wheelGestureTimer = null;
      }
    }

    releasePageAndApplyWheel(event) {
      this.clearWheelGesture();
      this.unlockPageScroll();
      // Apply this wheel delta once unlocked (overflow:hidden would otherwise swallow it).
      // Not a yank-back — only continues in the user's scroll direction.
      if (typeof event.deltaY === 'number' && event.deltaY !== 0) {
        window.scrollBy(0, event.deltaY);
      }
    }

    onWheel(event) {
      if (!this.isCarouselWheelEnabled()) return;
      if (!this.shouldHandleWheel(event)) return;
      if (Math.abs(event.deltaY) < 8) return;

      // Hard-freeze only in the geometric freeze zone (or if already locked).
      // Do not lock merely because the pointer is over the host while flying past.
      if (this.pageScrollLocked || this.shouldFreezePage()) {
        this.lockPageScroll();
      }

      const now = Date.now();
      const direction = event.deltaY > 0 ? 1 : -1;
      const nextIndex = this.currentIndex + direction;
      const canSwitch = nextIndex >= 0 && nextIndex <= this.slides.length - 1;
      const isAnimating = this.wheelAnimating || now < this.wheelLockedUntil;

      if (isAnimating) {
        event.preventDefault();
        this.refreshWheelGesture();
        return;
      }

      // Eat leftover trackpad inertia on the same gesture at first/last while frozen.
      if (this.pageScrollLocked && this.wheelGestureActive && !canSwitch) {
        event.preventDefault();
        this.refreshWheelGesture();
        return;
      }

      // Fresh outward gesture at first/last while frozen: unlock and continue page scroll.
      if (!canSwitch) {
        if (this.pageScrollLocked) {
          event.preventDefault();
          this.releasePageAndApplyWheel(event);
        }
        return;
      }

      event.preventDefault();
      this.refreshWheelGesture();
      this.wheelLockedUntil = now + this.wheelCooldownMs;
      this.wheelAnimating = true;
      if (this.wheelAnimTimer) window.clearTimeout(this.wheelAnimTimer);
      this.goToSlide(nextIndex);
      this.wheelAnimTimer = window.setTimeout(() => {
        this.wheelAnimating = false;
        this.wheelAnimTimer = null;
      }, this.slideTransitionMs);
    }

    onKeyDown(event) {
      if (!this.pageScrollLocked) return;
      if (!SCROLL_KEYS.has(event.key)) return;
      event.preventDefault();
    }

    onScroll() {
      if (this.isMobileMode() || this.prefersReducedMotion) return;

      this.maybeLockPageScroll();

      if (this.slides.length <= 1 || this.maxScroll <= 0) {
        this.setTrackOffset(0);
        return;
      }

      this.setTrackOffset(this.getSlideOffset(this.currentIndex));
    }

    onModeChange() {
      this.unlockPageScroll();
      this.clearWheelGesture();
      this.currentIndex = 0;
      this.section.classList.remove('is-slide-forward', 'is-slide-backward');
      this.measure();
      this.goToSlide(0);
      this.maybeLockPageScroll();
    }

    onResize() {
      this.syncHeaderOffset();
      this.measure();
      if (this.isMobileMode() || this.prefersReducedMotion) {
        this.unlockPageScroll();
        return;
      }
      this.maybeLockPageScroll();
    }

    bindEvents() {
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize);
      window.addEventListener('wheel', this.onWheel, { passive: false });
      window.addEventListener('keydown', this.onKeyDown, { passive: false });

      if (this.isMobile.addEventListener) {
        this.isMobile.addEventListener('change', this.onModeChange);
      } else {
        this.isMobile.addListener(this.onModeChange);
      }

      if ('IntersectionObserver' in window) {
        this.intersectionObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting && entry.intersectionRatio >= FREEZE_VISIBLE_RATIO) {
                this.maybeLockPageScroll();
              }
            });
          },
          { threshold: [FREEZE_VISIBLE_RATIO, 0.85, 1] }
        );
        this.intersectionObserver.observe(this.host);
      }

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.onResize());
        this.resizeObserver.observe(this.track);
        this.resizeObserver.observe(this.viewport);
        this.resizeObserver.observe(this.pin);
        if (this.host !== this.section) {
          this.resizeObserver.observe(this.host);
        }

        const storeHeader = document.querySelector('store-header');
        if (storeHeader) {
          this.headerObserver = new ResizeObserver(() => this.onResize());
          this.headerObserver.observe(storeHeader);
        }
      }

      this.track.querySelectorAll('img').forEach((img) => {
        if (img.complete) return;
        img.addEventListener('load', this.onResize, { once: true });
      });
    }

    destroy() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      window.removeEventListener('wheel', this.onWheel);
      window.removeEventListener('keydown', this.onKeyDown);

      if (this.isMobile.removeEventListener) {
        this.isMobile.removeEventListener('change', this.onModeChange);
      } else {
        this.isMobile.removeListener(this.onModeChange);
      }

      if (this.intersectionObserver) this.intersectionObserver.disconnect();
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.headerObserver) this.headerObserver.disconnect();

      if (this.wheelAnimTimer) window.clearTimeout(this.wheelAnimTimer);
      this.clearWheelGesture();
      this.wheelAnimating = false;
      this.wheelLockedUntil = 0;
      this.unlockPageScroll();

      this.scrollContainer.style.height = '';
      this.track.style.transform = '';
      this.section.classList.remove(
        'is-itshs-ready',
        'is-slide-forward',
        'is-slide-backward',
        'is-page-scroll-locked'
      );
      this.slides.forEach((slide) => {
        slide.style.flex = '';
        slide.style.width = '';
        slide.classList.remove('is-active');
      });
    }
  }

  function initImageTextScrollHoverSliders() {
    document.querySelectorAll('[data-image-text-scroll-hover-slider]').forEach((section) => {
      const id = section.id || section.dataset.sectionId;
      if (!id) return;

      if (instances.has(id)) {
        instances.get(id).destroy();
      }

      const instance = new ImageTextScrollHoverSlider(section);
      if (instance.pin) {
        instances.set(id, instance);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initImageTextScrollHoverSliders);
  } else {
    initImageTextScrollHoverSliders();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const target = event.target;
    if (
      !target.matches?.('[data-image-text-scroll-hover-slider]') &&
      !target.querySelector?.('[data-image-text-scroll-hover-slider]')
    ) {
      return;
    }
    initImageTextScrollHoverSliders();
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target.querySelector('[data-image-text-scroll-hover-slider]');
    if (!section?.id || !instances.has(section.id)) return;
    instances.get(section.id).destroy();
    instances.delete(section.id);
  });
})();
